import {
  ColorFormatting,
  ColorRangeFormatting,
  FormattingFilter,
  HexColor,
  ListFieldFormatting,
  ViewIds,
} from '@ardoq/api-types';
import {
  AUTOMATIC_COLOR_ASSIGNMENT_COLORS,
  PALE_AUTOMATIC_COLOR_ASSIGNMENT_COLORS,
  COLORS_FOR_COLOR_RANGE,
  PALE_COLORS_FOR_COLOR_RANGE,
} from '../../consts';
import {
  LIST_FIELD_DEFAULT_VALUE_START_TO_COLOR_MAP,
  LIST_FIELD_DEFAULT_VALUE_TO_COLOR_MAP,
  PALE_LIST_FIELD_DEFAULT_VALUE_START_TO_COLOR_MAP,
  PALE_LIST_FIELD_DEFAULT_VALUE_TO_COLOR_MAP,
} from './listFieldValueDefaultColors';
import { FormattingRule } from '@ardoq/data-model';
import {
  isColorRangeFormatting,
  isListFieldFormatting,
} from '@ardoq/api-types';

/** Checks if pale colors should be used based on viewId */
const shouldUsePaleColors = (viewId?: string): boolean =>
  viewId === ViewIds.DEPENDENCY_MAP_2;

type ColoredFormatting =
  | Pick<FormattingRule, 'color' | 'value'>
  | Pick<FormattingFilter, 'color' | 'value'>;

/** Collects all used colors colors for regular formatting rules without nested value structure */
export const collectUsedColors = (
  rules: ColoredFormatting[]
): Set<HexColor> => {
  const usedColorsSet = new Set<HexColor>();

  for (const rule of rules) {
    // Collect colors from nested structures of list/color-range formatting
    if (
      rule.value &&
      (isListFieldFormatting(rule.value) || isColorRangeFormatting(rule.value))
    ) {
      Object.values(rule.value).forEach(
        entry => entry && entry.color && usedColorsSet.add(entry.color)
      );
      // Collect colors from regular rules
    } else if (rule.color) {
      usedColorsSet.add(rule.color);
    }
  }

  return usedColorsSet;
};

/** Find the next available color that is not already used */
export const findNextAvailableColor = (
  usedColors: Set<HexColor>,
  viewId?: string
): HexColor => {
  const colorPalette = shouldUsePaleColors(viewId)
    ? PALE_AUTOMATIC_COLOR_ASSIGNMENT_COLORS
    : AUTOMATIC_COLOR_ASSIGNMENT_COLORS;

  for (const color of colorPalette) {
    if (!usedColors.has(color)) {
      return color;
    }
  }
  return colorPalette[usedColors.size % colorPalette.length];
};

/** Get the next available color for a new formatting rule */
export const getNextColorForFormattingRule = (
  rules: FormattingRule[],
  viewId?: string
): HexColor => findNextAvailableColor(collectUsedColors(rules), viewId);

const findLongestMatchingPrefix = (
  startToColorMap: Map<string, HexColor>,
  fieldValue: string
): HexColor | undefined => {
  const matchingEntries = Array.from(startToColorMap.entries()).filter(
    ([prefix]) => fieldValue.startsWith(prefix)
  );

  if (matchingEntries.length === 0) {
    return undefined;
  }

  const longestMatch = matchingEntries.reduce((longest, current) => {
    const [longestPrefix] = longest;
    const [currentPrefix] = current;
    return currentPrefix.length > longestPrefix.length ? current : longest;
  });

  const [, color] = longestMatch;
  return color;
};

const findPredefinedColor = (
  fieldName: string,
  fieldValue: string,
  viewId?: string
): HexColor | undefined => {
  const usePaleColors = shouldUsePaleColors(viewId);

  const startToColorMap = usePaleColors
    ? PALE_LIST_FIELD_DEFAULT_VALUE_START_TO_COLOR_MAP.get(fieldName)
    : LIST_FIELD_DEFAULT_VALUE_START_TO_COLOR_MAP.get(fieldName);
  if (startToColorMap) {
    const prefixMatch = findLongestMatchingPrefix(startToColorMap, fieldValue);
    if (prefixMatch) return prefixMatch;
  }

  const valueToColorMap = usePaleColors
    ? PALE_LIST_FIELD_DEFAULT_VALUE_TO_COLOR_MAP.get(fieldName)
    : LIST_FIELD_DEFAULT_VALUE_TO_COLOR_MAP.get(fieldName);
  return valueToColorMap?.get(fieldValue);
};

type AssignColorsState = {
  entries: Record<string, ColorFormatting>;
  usedColors: Set<HexColor>;
  fieldName: string;
  changed: boolean;
  viewId?: string;
};

const keepExistingEntry = (
  acc: AssignColorsState,
  fieldValue: string,
  data: ColorFormatting
): AssignColorsState => ({
  ...acc,
  entries: { ...acc.entries, [fieldValue]: data },
});

const assignPredefinedColor = (
  acc: AssignColorsState,
  fieldValue: string,
  data: ColorFormatting | undefined,
  predefinedColor: HexColor
): AssignColorsState => {
  acc.usedColors.add(predefinedColor);

  return {
    ...acc,
    entries: {
      ...acc.entries,
      [fieldValue]: {
        color: predefinedColor,
        isDisabled: Boolean(data?.isDisabled),
      },
    },
    changed: true,
  };
};

const assignAutomaticColor = (
  acc: AssignColorsState,
  fieldValue: string,
  data: ColorFormatting | undefined
): AssignColorsState => {
  const automaticColor = findNextAvailableColor(acc.usedColors, acc.viewId);
  acc.usedColors.add(automaticColor);

  return {
    ...acc,
    entries: {
      ...acc.entries,
      [fieldValue]: {
        color: automaticColor,
        isDisabled: Boolean(data?.isDisabled),
      },
    },
    changed: true,
  };
};

/** Assign colors to list field formatting entries that don't have a color assigned */
export const assignListColors = (
  fieldName: string,
  current: ListFieldFormatting,
  rules: ColoredFormatting[],
  viewId?: string
): ListFieldFormatting => {
  const initialState: AssignColorsState = {
    entries: {},
    usedColors: collectUsedColors(rules),
    fieldName,
    changed: false,
    viewId,
  };
  const result = Object.entries(current).reduce<AssignColorsState>(
    (acc, [fieldValue, data]) => {
      const predefinedColor = findPredefinedColor(
        acc.fieldName,
        fieldValue,
        acc.viewId
      );

      // If predefined color exists, use it (even if data?.color exists)
      if (predefinedColor) {
        return assignPredefinedColor(acc, fieldValue, data, predefinedColor);
      }

      // If no predefined color and no existing color, assign automatic color
      if (!data?.color) {
        return assignAutomaticColor(acc, fieldValue, data);
      }

      // Otherwise keep existing entry
      return keepExistingEntry(acc, fieldValue, data);
    },
    initialState
  );

  return result.changed ? { ...current, ...result.entries } : current;
};

export const getUsedCount = (
  column: HexColor[],
  usedColors: Set<HexColor>
): number =>
  Number(usedColors.has(column[0])) +
  Number(usedColors.has(column[2])) +
  Number(usedColors.has(column[4]));

/**
 * Get the next available color column for color range formatting
 * @param usedColors
 * @param viewId - Optional viewId to determine which color palette to use
 */
export const getNextColorsColumn = (
  usedColors: Set<HexColor>,
  viewId?: string
): { minColor: HexColor; midColor: HexColor; maxColor: HexColor } => {
  const colorRangePalette = shouldUsePaleColors(viewId)
    ? PALE_COLORS_FOR_COLOR_RANGE
    : COLORS_FOR_COLOR_RANGE;

  let bestColumnIndex = 0;
  let bestUsedCount = Number.POSITIVE_INFINITY;

  for (
    let columnIndex = 0;
    columnIndex < colorRangePalette.length;
    columnIndex++
  ) {
    const column = colorRangePalette[columnIndex];
    const usedCount = getUsedCount(column, usedColors);

    if (usedCount === 0) {
      return {
        minColor: column[0],
        midColor: column[2],
        maxColor: column[4],
      };
    }

    if (usedCount < bestUsedCount) {
      bestUsedCount = usedCount;
      bestColumnIndex = columnIndex;
    }
  }

  const fallbackColumn = colorRangePalette[bestColumnIndex];
  return {
    minColor: fallbackColumn[0],
    midColor: fallbackColumn[2],
    maxColor: fallbackColumn[4],
  };
};

/** Assign colors to color range formatting points (min/mid/max) */
export const assignColorRangeColors = (
  rules: FormattingRule[],
  range: ColorRangeFormatting,
  viewId?: string
): ColorRangeFormatting => {
  const usedColors = collectUsedColors(rules);
  const { minColor, midColor, maxColor } = getNextColorsColumn(
    usedColors,
    viewId
  );

  return {
    ...range,
    min: { ...range.min, color: minColor },
    mid: { ...range.mid, color: midColor },
    max: { ...range.max, color: maxColor },
  };
};
